import Select, { CSSObjectWithLabel, StylesConfig } from "react-select";
import { DateTime } from "luxon";
import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { MultipleComponentPopup } from "@/Common/Components/Modals/MultipleComponentPopup";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { ClientIdentifier } from "@/Common/Utilities/ClientIdentifier";
import { LinkingPopupService } from "./LinkingPopupService";

import "./LinkingPopup.css";

interface ILinkingPopupParams {
    requireAppointmentLink?: {
        suggestedAppointmentLinkId?: string;
        patientContactId: string;
    }

    requireOasisDocumentClassLink?: {
        suggestedOasisCode?: string;
    }
}

interface ILinkingPopupProps extends ILinkingPopupParams {
    submit: (additionalSignOffInformation: AdditionalSignOffInformation) => void;
    onCancel: () => void;
}

class LinkingPopupState {
    public appointments: PartialAppointmentInfo[];
    public selectedAppointment: PartialAppointmentInfo;
    public selectedCategory: string;
    public isLinking: boolean;
    public isLoaded: boolean;

    public constructor(fields?: Partial<LinkingPopupState>) {
        Object.assign(this, fields);
    }
}

class LinkingPopup extends BaseReactComponent<ILinkingPopupProps, LinkingPopupState> {
    public constructor(props: ILinkingPopupProps) {
        super(props);
        this.state = new LinkingPopupState({
            appointments: [],
            isLoaded: false,
            selectedCategory: props.requireOasisDocumentClassLink?.suggestedOasisCode ?? ""
        });
    }

    public async componentDidMount(): Promise<void> {
        this.setOverlay("linking-popup-overlay");
        if (this.props.requireAppointmentLink != null)
            await this.loadAppointments();
    }

    public render(): JSX.Element {
        return (
            <MultipleComponentPopup
                shouldCloseOnClickOutside={false}
                onCloseCallback={() => this.onCancel()}
                contentJustification="center"
                contentMarginTop={-30}
                containerCssClass="linking-popup-multiple-component-container"
            >
                <div className="popup linking-popup">
                    <div className="p-header">
                        <a className="close" aria-label="Close" onClick={() => this.onCancel()}> x</a>
                        <h4>{cacheStore.GetString("AdditionalInfoRequired")}</h4>
                    </div>
                    <div>
                        <div className="linking-popup-content">

                            <div>
                                {this.props.requireAppointmentLink && (
                                    this.renderAppointmentLink()
                                )}

                                {this.props.requireOasisDocumentClassLink &&
                                    this.renderOasisLink()
                                }
                            </div>
                        </div>
                    </div>
                    {this.renderFooter()}
                </div>
            </MultipleComponentPopup>
        );
    }

    private renderAppointmentLink(): JSX.Element {
        const options = (this.state.appointments || []).map(p => ({
            value: p.id,
            label: this.appointmentOptionControl(p)
        }));

        const customStyles: StylesConfig =
            {
                control: (provided: CSSObjectWithLabel) => ({
                    ...provided,
                    width: "100%",
                    textOverflow: "ellipsis"
                } as CSSObjectWithLabel),
                menu: (provided: CSSObjectWithLabel) => ({
                    ...provided,
                    margin: 0,
                    padding: 0
                } as CSSObjectWithLabel),
                indicatorSeparator: (provided: CSSObjectWithLabel) => ({
                    ...provided,
                    display: "none"
                } as CSSObjectWithLabel),
                option: (provided: any) => ({
                    ...provided,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap"
                } as CSSObjectWithLabel)

            };
        const selectedValue = this.state.selectedAppointment != null ?
            {
                value: this.state.selectedAppointment?.id,
                label: this.state.selectedAppointment?.name === cacheStore.GetString("SelectApproveNoteWithoutAppointment")
                    ? cacheStore.GetString("SelectApproveNoteWithoutAppointment")
                    : this.appointmentOptionControl(this.state.selectedAppointment)
            } : {};

        return (
            <>
                <label>{cacheStore.GetString("SelectAppointmentToLinkLabel")}</label>
                <Select
                    maxMenuHeight={180}
                    styles={customStyles}
                    value={selectedValue}
                    isSearchable={false}
                    onChange={this.handleChange.bind(this)}
                    options={options}
                />
                {this.state.isLoaded && !this.state.appointments?.filter(p => p.name !== cacheStore.GetString("SelectApproveNoteWithoutAppointment")).any() &&
                this.state.selectedAppointment?.name !== cacheStore.GetString("SelectApproveNoteWithoutAppointment") &&
                    <label>{cacheStore.GetString("NoAppointmentToLinkLabel")}</label>
                }
            </>
        );
    }

    private handleChange(selectedOption: { value: string; label: string } | null): void {
        this.mutateState(s => {
            s.selectedAppointment = this.state.appointments.firstOrDefault(
                p => p.id === selectedOption?.value
            );
        });
    }
    private appointmentOptionControl(model?: PartialAppointmentInfo): JSX.Element {
        if (model?.name == cacheStore.GetString("SelectApproveNoteWithoutAppointment"))
            return (
                <>
                    {cacheStore.GetString("SelectApproveNoteWithoutAppointment")}
                </>
            );

        const subject = this.getSubjectText(model?.name);
        return (
            <>
                <span className="text-bold">
                    {DateHelper.formatIsoDateOnly(model?.startDate)}
                </span>
                <span>
                    {` ${cacheStore.GetString("AtLabel")} ${moment(model?.startDate).format("HH:mm:ss")} `}
                </span>
                <span className="text-bold" title={model?.provider}>
                    {model?.provider}
                </span>
                {subject && (
                    <span title={subject}>
                        {` - ${subject}`}
                    </span>
                )}
            </>
        );
    }

    private renderOasisLink(): JSX.Element {
        const options = ClientIdentifier.IsCemtl ? [
            { value: "", display: "" },
            { value: "CLAS14", display: "DEPENDANCE" },
            { value: "CLAS21", display: "MAISON BLEUE" },
            { value: "CLAS22", display: "MAISON DES NAISSANCES" },
            { value: "CLAS23", display: "JEUNESSE" },
            { value: "CLAS27", display: "MEDICAL incluant triage" },
            { value: "CLAS28", display: "SERVICES GENERAUX SANTE" }
        ] : [];

        return (
            <div style={{ marginBottom: "25px" }}>
                <label>{cacheStore.GetString("SelectCategory")}</label>
                <select
                    className="form-control text-field-width"
                    value={this.state.selectedCategory}
                    onChange={(e: any) => {
                        this.mutateState(s => {
                            s.selectedCategory = e.target?.value;
                        });
                    }}
                >
                    {options.map(p => <option key={`link-note-to-category-${p.value}`} value={p.value}>{p.display}</option>)}
                </select>
            </div>
        );
    }
    private getSubjectText(subject?: string): string {
        subject = subject?.trim();
        if (subject?.endsWith("-"))
            subject = subject.slice(0, -1);

        return subject ?? "";
    }

    private renderFooter(): JSX.Element {
        const isDisabled = (
            this.state.isLinking
            || (this.state.selectedAppointment == null && !!this.props.requireAppointmentLink)
            || (this.state.selectedCategory == "" && !!this.props.requireOasisDocumentClassLink)
        );

        return (
            <div className="p-footer">
                <div className="footer-button-container">
                    <ButtonControl
                        className="btn btn-default"
                        onClick={() => this.onCancel()}
                        label={this.getString("CancelLabel")}
                    />
                    <ButtonControl
                        variant="brand"
                        label={this.state.selectedAppointment?.name === cacheStore.GetString("SelectApproveNoteWithoutAppointment") ? cacheStore.GetString("ContinueLabel") : cacheStore.GetString("LinkLabel")}
                        disabled={isDisabled}
                        onClick={() => this.link()}
                    />
                </div>
            </div>
        );
    }

    private async loadAppointments(): Promise<void> {
        if (!this.props.requireAppointmentLink?.patientContactId)
            return;

        const response = await LinkingPopupService.loadAppointments(
            this.props.requireAppointmentLink.patientContactId,
            DateTime.local().plus({ days: 1 })
        );

        this.mutateState(s => {
            s.isLoaded = true;
            const loadedAppointments = response.data.AppointmentList?.map(appointment => {
                const itemModel = new PartialAppointmentInfo({
                    name: appointment.Subject,
                    id: appointment.AppointmentId,
                    startDate: appointment.StartDate,
                    endDate: appointment.EndDate,
                    provider: appointment.EmployeeAndRoomId
                        ?.split(";")
                        .map(id => id && cacheStore.GetCliniciansPersonsByContactFacetId(id.toLowerCase())?.NameWithPrefix)
                        .filter(Boolean) // Remove undefined/null values
                        .join(", ") || ""
                });
                return itemModel;
            }) || [];
            const customNoAppointmentInfo = new PartialAppointmentInfo({ name: cacheStore.GetString("SelectApproveNoteWithoutAppointment") });
            s.appointments = cacheStore.ClinicSettings.EnableOptionSignNoteWithoutAppointment ? [customNoAppointmentInfo, ...loadedAppointments] : loadedAppointments;
            const filteredAppointment = this.props.requireAppointmentLink.suggestedAppointmentLinkId ?
                loadedAppointments?.filter(p => p.id?.toLowerCase() === this.props.requireAppointmentLink.suggestedAppointmentLinkId?.toLowerCase())?.firstOrDefault() :
                loadedAppointments?.sortByPropertyDesc(p => p.endDate)?.firstPreferredOrDefault(p => DateHelper.formatDateLuxon(p.endDate, "yyyy-MM-dd") == DateTime.local().toFormat("yyyy-MM-dd"));

            s.selectedAppointment = filteredAppointment;
        });
    }

    private link(): void {
        // remember last selected value for suggestions
        sessionStore.lastSelectedNoteExternalCategory = this.state.selectedCategory;

        this.mutateState(s => {
            // To prevent spam click
            // The compenent will stay locked and the user will have to close the popup and reopen it to re-attemp to link
            // another solution would be to handle on callback in the onlink() from props.
            s.isLinking = true;
        }, () => {
            this.props.submit(new AdditionalSignOffInformation({
                appointmentId: this.state.selectedAppointment?.id,
                interfaceNoteCategory: this.state.selectedCategory

            }));
        });
    }

    public onCancel(): void {
        this.props.onCancel();
        LinkingPopupService.CloseLinkingPopup();
    }
}

class PartialAppointmentInfo {
    public name: string;
    public id: string;
    public startDate: Date;
    public endDate: Date;
    public provider: string;

    public constructor(fields?: Partial<PartialAppointmentInfo>) {
        Object.assign(this, fields);
    }
}

class AdditionalSignOffInformation {
    public constructor(fields: Partial<AdditionalSignOffInformation>) {
        Object.assign(this, fields);
    }

    public appointmentId: string;
    public interfaceNoteCategory: string;

    public ValidationCallback: (success: boolean) => void;
}

export { AdditionalSignOffInformation, ILinkingPopupParams, ILinkingPopupProps, LinkingPopup };
